import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LogOut, User as UserIcon } from "lucide-react";
import Wordmark from "../Brand/Wordmark";
import CompanySwitcher from "../Company/CompanySwitcher";

interface HeaderProps {
  userType?: "jobseeker" | "hr" | "admin";
}

/*
 * Velaigal post-auth header.
 * Cream background, hairline bottom border, no glass blur, no gradients.
 * Per spec §4.8.
 */
const Header: React.FC<HeaderProps> = ({ userType }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-40 bg-cream border-b border-hairline">
      <div className="flex items-center justify-between px-8 h-[72px]">
        <Link
          to="/"
          aria-label="Velaigal"
          className="text-ink no-underline"
        >
          <Wordmark variant="lockup" size="md" animateOnMount />
        </Link>

        <div className="flex items-center gap-4">
          {/* Renders nothing unless the session holds more than one active membership, so the
              single-company header is byte-for-byte what it was. */}
          {userType === "hr" && <CompanySwitcher />}

          {userType === "hr" && (
            <Link
              to="/hr/create-job"
              className="btn-primary no-underline"
            >
              Create job
            </Link>
          )}

          <div className="flex items-center gap-3 pl-4 border-l border-hairline">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-medium text-ink leading-tight">
                {user?.name ?? "Guest"}
              </span>
              {userType && (
                <span className="text-[11px] uppercase tracking-widest text-ink-tertiary capitalize">
                  {userType === "hr" ? "Recruiter" : userType}
                </span>
              )}
            </div>
            <button
              type="button"
              aria-label="Profile"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-saffron-tint text-saffron-press hover:bg-saffron hover:text-cream transition-colors"
            >
              <UserIcon className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              className="flex h-9 w-9 items-center justify-center text-ink-tertiary hover:text-ink transition-colors"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
